import { ISort } from '../data/types';

export interface IExpression {
  operator: string;
  [key: string]: any;
}

export interface ISerializer<I = any, O = any> {
  serialize: (filters: I) => O;
  deserialize(filter: O): I | undefined;
}

abstract class FilterSerializer<O = any> implements ISerializer<IExpression, O> {
  private visitors = new Map<string, (f: any) => any>();

  protected define(operator: string, func: (f: any) => any): void {
    this.visitors.set(operator, func);
  }
  protected resolve(operator: string, filter: any): any {
    return this.visitors.get(operator)?.(filter);
  }

  abstract serialize(filter: IExpression): any;
  abstract deserialize(filter: any): IExpression | undefined;
}

class FilterService {
  private serializers = new Map<string, FilterSerializer>();

  define(key: string, serializer: FilterSerializer): void {
    this.serializers.set(key, serializer);
  }

  find(key: string): FilterSerializer | undefined {
    return this.serializers.get(key);
  }

  transform(filter: any, from: string, to: string): any {
    const src = this.serializers.get(from);
    if (!src) throw new Error(`Serializer "${from}" is not defined`);

    const dest = this.serializers.get(to);
    if (!dest) throw new Error(`Serializer "${to}" is not defined`);

    const expression = src.deserialize(filter);
    if (!expression) return;

    return dest.serialize(expression);
  }

  serialize(format: string, filter: IExpression): any {
    return this.serializers.get(format)?.serialize(filter);
  }
  deserialize(format: string, filter: any): IExpression | undefined {
    return this.serializers.get(format)?.deserialize(filter);
  }

  sorter(sort?: ISort): string | undefined { return (!!sort?.field ? `${sort.field}:${sort.order ?? 'asc'}` : undefined); }
}

export class DXFilterSerializer extends FilterSerializer {
  constructor() {
    super();

    const serialize = (o: any) => this.serialize(o);
    const deserialize = (o: any) => this.deserialize(o);

    this.define('out(=)', ({ field, value }) => [field, '=', value]);
    this.define('out(!=)', ({ field, value }) => [field, '<>', value]);
    this.define('out(>)', ({ field, value }) => [field, '>', value]);
    this.define('out(>=)', ({ field, value }) => [field, '>=', value]);
    this.define('out(<)', ({ field, value }) => [field, '<', value]);
    this.define('out(<=)', ({ field, value }) => [field, '<=', value]);

    this.define('out(?)', ({ field, value }) => [field, 'in', value]);
    this.define('out(-)', ({ field, value }) => [field, 'between', value]);
    this.define('out(*)', ({ field, value }) => [field, 'contains', value]);
    this.define('out(^)', ({ field, value }) => [field, 'startswith', value]);
    this.define('out($)', ({ field, value }) => [field, 'endswith', value]);

    this.define('out(ε)', ({ field }) => [field, 'isblank', null]);
    this.define('out(!ε)', ({ field }) => [field, 'isnotblank', null]);

    this.define('out(&)', ({ filters }) => this.join('and', filters.map(serialize)));
    this.define('out(|)', ({ filters }) => this.join('or', filters.map(serialize)));
    this.define('out(!)', ({ filters }) => ['!', filters.map(serialize)]);

    this.define('in(=)', ([field, , value]) => ({ operator: '=', field, value }));
    this.define('in(<>)', ([field, , value]) => ({ operator: '!=', field, value }));
    this.define('in(>)', ([field, , value]) => ({ operator: '>', field, value }));
    this.define('in(>=)', ([field, , value]) => ({ operator: '>=', field, value }));
    this.define('in(<)', ([field, , value]) => ({ operator: '<', field, value }));
    this.define('in(<=)', ([field, , value]) => ({ operator: '<=', field, value }));

    this.define('in(in)', ([field, , value]) => ({ operator: '?', field, value }));
    this.define('in(between)', ([field, , value]) => ({ operator: '-', field, value }));
    this.define('in(contains)', ([field, , value]) => ({ operator: '*', field, value }));
    this.define('in(startswith)', ([field, , value]) => ({ operator: '^', field, value }));
    this.define('in(endswith)', ([field, , value]) => ({ operator: '$', field, value }));

    this.define('in(isblank)', ([field]) => ({ operator: 'ε', field }));
    this.define('in(isnotblank)', ([field]) => ({ operator: '!ε', field }));

    this.define('in(and)', (filters) => ({ operator: '&', filters: filters.map(deserialize) }));
    this.define('in(or)', (filters) => ({ operator: '|', filters: filters.map(deserialize) }));

    this.define('in(!)', ([, filters]) => ({ operator: '!', filters: filters.map(deserialize) }));
  }

  private join(operator: string, filters: any[]): any[] {
    if (filters.length <= 1) return filters[0];

    return filters.reduce<any[]>((result, filter, i) => {
      if (i > 0) result.push(operator);
      result.push(filter);

      return result;
    }, []);
  }

  serialize(filter: IExpression): any {
    const operator = filter.operator;

    return this.resolve(`out(${operator})`, filter);
  }

  deserialize(filter: any): IExpression | undefined {
    if (!filter?.length) return { operator: '&', filters: [] };

    const operator = filter[[2].includes(filter.length) ? 0 : 1];
    if (!operator) return { operator: '&', filters: filter.map((o: any) => this.deserialize(o)) };

    if (['and', 'or'].includes(operator)) filter = filter.filter((o: any) => (o !== operator));

    return this.resolve(`in(${operator})`, filter);
  }
}
export class JQFilterSerializer extends FilterSerializer {
  constructor() {
    super();

    const serialize = (o: any) => this.serialize(o, false);
    const deserialize = (o: any) => this.deserialize(o);

    this.define('out(=)', ({ value }) => (value));

    this.define('out(?)', ({ value }) => (value));

    this.define('out(&)', ({ filters }) => ({ 'and': filters.map(serialize) }));

    this.define('in(equals)', ([field, { value }]) => ({ operator: '=', field, value }));

    this.define('in(in)', ([field, { value }]) => ({ operator: '?', field, value }));

    this.define('in(and)', ({ constraints }) => ({ operator: '&', filters: constraints.map(deserialize) }));
  }

  serialize(filter: IExpression, root: boolean = true): any {
    const operator = filter.operator;
    if (root && ['&', '|', '!'].includes(operator)) {
      const { filters } = filter;

      return Object.fromEntries(filters.map((o: IExpression) => [o.field, this.serialize(o)]));
    }

    return this.resolve(`out(${operator})`, filter);
  }

  deserialize(filter: any): IExpression | undefined {
    if (!filter) return { operator: '&', filters: [] };

    const operator = (Array.isArray(filter) ? (Array.isArray(filter[1]) ? 'in' : 'equals') : undefined);
    if (!operator) return { operator: '&', filters: Object.entries<any>(filter).filter(o => ![null, undefined].includes(o[1]?.value)).map((o) => this.deserialize(o)) };

    return this.resolve(`in(${operator})`, filter);
  }
}
export class PRFilterSerializer extends FilterSerializer {
  constructor() {
    super();

    const serialize = (o: any) => this.serialize(o, false);
    const deserialize = (o: any) => this.deserialize(o);

    this.define('out(=)', ({ value }) => ({ value, matchMode: 'equals' }));
    this.define('out(!=)', ({ value }) => ({ value, matchMode: 'notEquals' }));
    this.define('out(>)', ({ value }) => ({ value, matchMode: 'gt' }));
    this.define('out(>=)', ({ value }) => ({ value, matchMode: 'gte' }));
    this.define('out(<)', ({ value }) => ({ value, matchMode: 'lt' }));
    this.define('out(<=)', ({ value }) => ({ value, matchMode: 'lte' }));

    this.define('out(?)', ({ value }) => ({ value, matchMode: 'in' }));
    this.define('out(!?)', ({ value }) => ({ value, matchMode: 'notIn' }));
    this.define('out(-)', ({ value }) => ({ value, matchMode: 'between' }));
    this.define('out(*)', ({ value }) => ({ value, matchMode: 'contains' }));
    this.define('out(!*)', ({ value }) => ({ value, matchMode: 'notContains' }));
    this.define('out(^)', ({ value }) => ({ value, matchMode: 'startsWith' }));
    this.define('out($)', ({ value }) => ({ value, matchMode: 'endsWith' }));

    this.define('out(ε)', () => ({ value: null, matchMode: 'isBlank' }));
    this.define('out(!ε)', () => ({ value: null, matchMode: 'isNotBlank' }));

    this.define('out(&)', ({ filters }) => ({ operator: 'and', constraints: filters.map(serialize) }));
    this.define('out(|)', ({ filters }) => ({ operator: 'or', constraints: filters.map(serialize) }));
    this.define('out(!)', ({ filters }) => ({ operator: 'not', constraints: filters.map(serialize) }));

    this.define('in(equals)', ([field, { value }]) => ({ operator: '=', field, value }));
    this.define('in(notEquals)', ([field, { value }]) => ({ operator: '!=', field, value }));
    this.define('in(>)', ([field, { value }]) => ({ operator: '>', field, value }));
    this.define('in(>=)', ([field, { value }]) => ({ operator: '>=', field, value }));
    this.define('in(<)', ([field, { value }]) => ({ operator: '<', field, value }));
    this.define('in(<=)', ([field, { value }]) => ({ operator: '<=', field, value }));

    this.define('in(in)', ([field, { value }]) => ({ operator: '?', field, value }));
    this.define('in(between)', ([field, { value }]) => ({ operator: '-', field, value }));
    this.define('in(contains)', ([field, { value }]) => ({ operator: '*', field, value }));
    this.define('in(notContains)', ([field, { value }]) => ({ operator: '!*', field, value }));
    this.define('in(startsWith)', ([field, { value }]) => ({ operator: '^', field, value }));
    this.define('in(endsWith)', ([field, { value }]) => ({ operator: '$', field, value }));

    this.define('in(isBlank)', ([field]) => ({ operator: 'ε', field }));
    this.define('in(isNotBlank)', ([field]) => ({ operator: '!ε', field }));

    this.define('in(and)', ({ constraints }) => ({ operator: '&', filters: constraints.map(deserialize) }));
    this.define('in(or)', ({ constraints }) => ({ operator: '|', filters: constraints.map(deserialize) }));
    this.define('in(not)', ({ constraints }) => ({ operator: '!', filters: constraints.map(deserialize) }));
  }

  serialize(filter: IExpression, root: boolean = true): any {
    const operator = filter.operator;
    if (root && ['&', '|', '!'].includes(operator)) {
      const { filters } = filter;

      return Object.fromEntries(filters.map((o: IExpression) => [o.field, this.serialize(o)]));
    }

    return this.resolve(`out(${operator})`, filter);
  }

  deserialize(filter: any): IExpression | undefined {
    if (!filter) return { operator: '&', filters: [] };

    const operator = (Array.isArray(filter) ? (filter[1]?.matchMode ?? 'equals') : filter.operator);
    if (!operator) return { operator: '&', filters: Object.entries<any>(filter).filter(o => ![null, undefined].includes(o[1]?.value)).map((o) => this.deserialize(o)) };

    return this.resolve(`in(${operator})`, filter);
  }
}

export const serializers = new FilterService();
serializers.define('dx', new DXFilterSerializer());
serializers.define('jq', new JQFilterSerializer());
serializers.define('pr', new PRFilterSerializer());
